/**
 * Datadog telemetry for frontend feature-flag evaluations.
 *
 * The backend already reports every OpenFeature evaluation to Datadog from two
 * globally-registered hooks (https://github.com/oscerai/ml-scribe/tree/main/shared/openfeature).
 * This module is the frontend counterpart with similar attribute vocabulary.
 *
 * | Attribute                          | Meaning                                                      |
 * | ---------------------------------- | ------------------------------------------------------------ |
 * | `feature_flag.key`                 | Raw flag key, e.g. `release-scribe-enable-offline-recording` |
 * | `feature_flag.provider.name`       | `FlagsmithProvider`, `OfrepProvider`, `unknown`              |
 * | `feature_flag.result.value`        | Resolved value, stringified; `unknown` when absent           |
 * | `feature_flag.result.reason`       | `CACHED`, `STATIC`, `DEFAULT`, `ERROR`                       |
 * | `feature_flag.evaluation.duration` | Milliseconds spent resolving; absent when unmeasured         |
 * | `error.type`                       | Error-path only                                              |
 *
 * Comparison attributes from `CustomComparisonStrategy`. Agreement and mismatch
 * compare the winning authority with the optional shadow:
 *
 * | Attribute                                 | Meaning                                            |
 * | ----------------------------------------- | -------------------------------------------------- |
 * | `feature_flag.comparison.agreement`       | Both providers answered with equal values          |
 * | `feature_flag.comparison.mismatch`        | Shadow served a different value; never `false`     |
 * | `feature_flag.comparison.failed_provider` | Provider that could not answer                     |
 * | `feature_flag.comparison.failed_code`     | `FLAG_NOT_FOUND` = not carried there yet; any      |
 * |                                           | other `ErrorCode` = that provider is unhealthy     |
 * | `feature_flag.comparison.shadow_value`    | What the shadow said; mismatch-only                |
 * | `feature_flag.comparison.shadow_reason`   | Shadow's reason, on a value OR reason divergence   |
 * | `feature_flag.comparison.pending`         | Shadow had no snapshot yet; not a verdict, so      |
 * |                                           | excluded from match-rate numerator and denominator |
 *
 * One sink: a `feature_flag_evaluation_success` / `_error` RUM action carrying
 * those attributes is sent to Datadog.
 *
 * Dashboards:
 * - FE: https://us3.datadoghq.com/dashboard/ug4-yz8-dxy/feature-flag-evaluations-fe
 * - BE: https://us3.datadoghq.com/dashboard/64z-ytn-mzs/feature-flag-evaluations
 * - FE agreement: filter RUM actions by `@action.name:feature_flag_evaluation_success`
 *   and `@feature_flag.comparison.agreement:true`. Match rate uses agreement
 *   actions / (agreement + mismatch actions), not all evaluation successes.
 *   Actions are deduped per flag/value type/provider within a browser session.
 */
import { isSaasTelemetryEnabled } from '@/utils/buildProfile';
import { datadogRum } from '@datadog/browser-rum';
import type { Hook } from '@openfeature/web-sdk';

export const FLAG_EVALUATION_SUCCESS_ACTION = 'feature_flag_evaluation_success';
export const FLAG_EVALUATION_ERROR_ACTION = 'feature_flag_evaluation_error';

export const MAX_TRACKED_FLAGS = 500;

const MAX_VALUE_LENGTH = 200;

const UNKNOWN = 'unknown';

/**
 * Last reported signature per `provider|valueType|key`.
 *
 * Flags are read during render, so the same evaluation recurs thousands of
 * times a session — without this the RUM action volume is unshippable. Keyed
 * by provider so a serving-provider change re-emits rather than being treated
 * as a duplicate. Keyed by value type so Flagsmith enabled-state and payload
 * reads of the same key don't flip each other's signature.
 */
const lastReported = new Map<string, string>();

export type FlagEvaluation = {
  key: string;
  value: unknown;
  reason?: string;
  provider?: string;
  valueType?: string;
  errorType?: string;
  durationMs?: number;
  agreement?: boolean;
  mismatch?: boolean;
  failedProvider?: string;
  failedCode?: string;
  shadowValue?: string;
  shadowReason?: string;
  shadowPending?: boolean;
};

const isPrimitive = (value: unknown): boolean =>
  value !== null && value !== undefined && typeof value !== 'object';

export const serialiseValue = (value: unknown): string => {
  if (isPrimitive(value)) return String(value).slice(0, MAX_VALUE_LENGTH);
  if (value === null || value === undefined) return UNKNOWN;
  try {
    return JSON.stringify(value).slice(0, MAX_VALUE_LENGTH);
  } catch {
    return UNKNOWN;
  }
};

export function reportFlagEvaluation(evaluation: FlagEvaluation): void {
  // TELEMETRY-GATE: the sink is a Datadog RUM action.
  if (!isSaasTelemetryEnabled) return;

  try {
    const {
      key,
      value,
      reason,
      provider,
      valueType,
      errorType,
      durationMs,
      agreement,
      mismatch,
      failedProvider,
      failedCode,
      shadowValue,
      shadowReason,
      shadowPending,
    } = evaluation;
    if (!key) return;

    const serialised = serialiseValue(value);
    const resolvedReason = reason || (errorType ? 'ERROR' : UNKNOWN);
    const providerName = provider ?? UNKNOWN;
    const trackingKey = `${providerName}|${valueType ?? UNKNOWN}|${key}`;
    // Verdict included, or a mismatch flipping under an unchanged value dedupes away.
    const signature = [
      serialised,
      resolvedReason,
      errorType,
      agreement,
      mismatch,
      failedProvider,
      failedCode,
      shadowValue,
      shadowReason,
      shadowPending,
    ].join('|');

    if (lastReported.get(trackingKey) === signature) return;

    const comparison = {
      ...(agreement ? { agreement } : {}),
      ...(mismatch ? { mismatch } : {}),
      ...(failedProvider ? { failed_provider: failedProvider } : {}),
      ...(failedCode ? { failed_code: failedCode } : {}),
      ...(shadowValue === undefined ? {} : { shadow_value: serialiseValue(shadowValue) }),
      ...(shadowReason ? { shadow_reason: shadowReason } : {}),
      ...(shadowPending ? { pending: true } : {}),
    };

    // Datadog's native `addFeatureFlagEvaluation` writes onto the RUM view —
    // good for "which flags were on when this session errored", but not for a
    // countable stream. A custom action is used instead so we keep the raw key
    // plus provider/reason/error as tags.
    datadogRum.addAction(
      errorType ? FLAG_EVALUATION_ERROR_ACTION : FLAG_EVALUATION_SUCCESS_ACTION,
      {
        feature_flag: {
          key,
          provider: { name: providerName },
          result: { value: serialised, reason: resolvedReason },
          ...(durationMs === undefined ? {} : { evaluation: { duration: durationMs } }),
          ...(Object.keys(comparison).length === 0 ? {} : { comparison }),
        },
        ...(errorType ? { error: { type: errorType } } : {}),
      }
    );

    // Record after emit so a swallowed RUM throw doesn't suppress retries.
    // Insertion-order eviction (not LRU): a hot unchanged flag stays oldest.
    lastReported.delete(trackingKey);
    while (lastReported.size >= MAX_TRACKED_FLAGS) {
      const oldest = lastReported.keys().next().value;
      if (oldest === undefined) break;
      lastReported.delete(oldest);
    }
    lastReported.set(trackingKey, signature);
  } catch {
    // Never break a flag read.
  }
}

/** Registry key for the domainless (default) provider. */
const DEFAULT_DOMAIN = '';

export const FLAGSMITH_PROVIDER_NAME = 'FlagsmithProvider';
export const OFREP_PROVIDER_NAME = 'OfrepProvider';
// Reported when no flag key is configured and every flag serves its call-site
// default, so that posture is a filterable provider name rather than `unknown`.
export const DEFAULTS_ONLY_PROVIDER_NAME = 'DefaultsOnlyProvider';

/**
 * Domain → stable provider name, recorded at `setProvider` time.
 *
 * The SDK's own `metadata.name` is derived from the JS class object and the
 * minifier rewrites it, so it would never match the backend's. A domain
 * string is a literal this app passes in, so it survives minification.
 */
const providerNamesByDomain = new Map<string, string>();

export const registerFlagProviderName = (
  domain: string | undefined,
  providerName: string
): void => {
  providerNamesByDomain.set(domain ?? DEFAULT_DOMAIN, providerName);
};

const resolveProviderName = (
  domain: string | undefined,
  flagMetadataProvider?: unknown
): string | undefined => {
  if (typeof flagMetadataProvider === 'string') return flagMetadataProvider;
  return (
    providerNamesByDomain.get(domain ?? DEFAULT_DOMAIN) ??
    providerNamesByDomain.get(DEFAULT_DOMAIN)
  );
};

const START_TIME_KEY = 'flagEvaluationTelemetry.startTime';

const readComparison = (
  flagMetadata?: Readonly<Record<string, string | number | boolean>>
): Pick<
  FlagEvaluation,
  | 'agreement'
  | 'mismatch'
  | 'failedProvider'
  | 'failedCode'
  | 'shadowValue'
  | 'shadowReason'
  | 'shadowPending'
> => {
  const asString = (field: string | number | boolean | undefined) =>
    field === undefined ? undefined : String(field);
  return {
    agreement: flagMetadata?.agreement === true,
    mismatch: flagMetadata?.mismatch === true,
    failedProvider: asString(flagMetadata?.failedProvider),
    failedCode: asString(flagMetadata?.failedCode),
    shadowValue: asString(flagMetadata?.shadowValue),
    shadowReason: asString(flagMetadata?.shadowReason),
    shadowPending: flagMetadata?.shadowPending === true,
  };
};

type TelemetryHookContext = Parameters<NonNullable<Hook['after']>>[0];

const readDurationMs = (hookContext: TelemetryHookContext): number | undefined => {
  try {
    const startTime = hookContext.hookData?.get(START_TIME_KEY);
    if (typeof startTime !== 'number') return undefined;

    const durationMs = performance.now() - startTime;
    if (!Number.isFinite(durationMs) || durationMs < 0) return undefined;

    return Math.round(durationMs * 1000) / 1000;
  } catch {
    return undefined;
  }
};

/**
 * Global OF hook.
 *
 * Duration is read at the top of `after`/`error` rather than `finally`, which
 * would fold this module's own `addAction` into the measurement. The two are
 * wildly mismatched in cost — the evaluation is a map lookup, where `addAction`
 * serialises a payload and hands it to the RUM SDK.
 */
export function createFlagEvaluationTelemetryHook(): Hook {
  return {
    before(hookContext) {
      try {
        hookContext.hookData?.set(START_TIME_KEY, performance.now());
      } catch {}
    },
    after(hookContext, evaluationDetails) {
      const durationMs = readDurationMs(hookContext);
      reportFlagEvaluation({
        key: hookContext.flagKey,
        value: evaluationDetails.value,
        reason: evaluationDetails.reason,
        valueType: hookContext.flagValueType,
        provider: resolveProviderName(
          hookContext.clientMetadata?.domain,
          evaluationDetails.flagMetadata?.provider
        ),
        durationMs,
        ...readComparison(evaluationDetails.flagMetadata),
      });
    },
    error(hookContext, error) {
      const durationMs = readDurationMs(hookContext);
      reportFlagEvaluation({
        key: hookContext.flagKey,
        value: hookContext.defaultValue,
        valueType: hookContext.flagValueType,
        provider: resolveProviderName(hookContext.clientMetadata?.domain),
        errorType: error instanceof Error ? error.name : 'Error',
        durationMs,
      });
    },
  };
}

export const __resetFlagEvaluationTelemetryForTests = (): void => {
  lastReported.clear();
  providerNamesByDomain.clear();
};
